import { useCallback, useEffect, useState } from "react";
import moment from "moment/min/moment-with-locales";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import i18n from "src/i18n";

import { CONSTANTS } from "@constants/index";
import { LanguageSelectValue } from "@models/shared/shared.model";
import handleResponse from "@services/apiConfig/api.response.service";
import API_SERVICES from "@services/index";
import { userSlice } from "@slices/user.slice";
import { RootState } from "@store/index";

export default function LanguageSelect() {
  const [language, setLanguage] = useState<LanguageSelectValue>({
    label: i18n.language?.toLocaleUpperCase(),
    value: i18n.language,
  });

  const dispatch = useDispatch();

  const { isAuthenticated } = useSelector((state: RootState) => state.local);
  const { selectedLanguage } = useSelector((state: RootState) => state.user);

  const languages = [
    {
      label: CONSTANTS.LANGUAGES.DE,
      value: CONSTANTS.LANGUAGES.DE?.toLocaleLowerCase(),
    },
    {
      label: CONSTANTS.LANGUAGES.FR,
      value: CONSTANTS.LANGUAGES.FR?.toLocaleLowerCase(),
    },
    {
      label: CONSTANTS.LANGUAGES.IT,
      value: CONSTANTS.LANGUAGES.IT?.toLocaleLowerCase(),
    },
    {
      label: CONSTANTS.LANGUAGES.EN,
      value: CONSTANTS.LANGUAGES.EN?.toLocaleLowerCase(),
    },
  ];

  useEffect(() => {
    initialLanguageHandle();
    moment.locale(selectedLanguage?.toLocaleLowerCase());
  }, [selectedLanguage]);

  useEffect(() => {
    if (isAuthenticated) {
      getUserSelectedLanguage();
    }
  }, [isAuthenticated]);

  const getUserSelectedLanguage = useCallback(async () => {
    const data = await API_SERVICES.USER.SELECTED_LANGUAGE();
    const responseData = handleResponse(data);
    if (responseData?.ok) {
      dispatch(
        userSlice.actions.userSelectedLanguage({
          active_language: responseData?.data?.data?.active_language,
        })
      );
    }
  }, []);

  const handleLanguageChange = (
    selectedValue: LanguageSelectValue | null
  ): void => {
    if (selectedValue) {
      const changedLanguage = selectedValue?.value;
      setLanguage(selectedValue);
      i18n.changeLanguage(changedLanguage);

      if (isAuthenticated) updateUserSelectedLanguage(changedLanguage);
    }
  };

  const initialLanguageHandle = () => {
    const selectedLanguageLowerCase = selectedLanguage.toLocaleLowerCase();
    if (selectedLanguage) {
      setLanguage({
        label: selectedLanguageLowerCase?.toLocaleUpperCase(),
        value: selectedLanguageLowerCase,
      });
      i18n.changeLanguage(selectedLanguageLowerCase);
    } else
      setLanguage({
        label: i18n.language?.toLocaleUpperCase(),
        value: i18n.language,
      });
  };

  const updateUserSelectedLanguage = useCallback(async (language: string) => {
    const data = await API_SERVICES.USER.UPDATE_LANGUAGE(language);
    const responseData = handleResponse(data);
    if (responseData?.ok) {
      getUserSelectedLanguage();
    }
  }, []);

  return (
    <div data-testid="language-switcher-select">
      <Select
        className="fg-select-language-switch"
        classNames={{
          dropdownIndicator: () => "fg-select-language-dropdown-indicator",
          valueContainer: () => "fg-select-language-value-container",
        }}
        theme={theme => {
          return {
            ...theme,
            borderRadius: 2,
            colors: {
              ...theme.colors,
              primary: "#3269e0",
            },
          };
        }}
        value={language}
        defaultValue={language}
        onChange={newValue => handleLanguageChange(newValue)}
        isSearchable={false}
        name="fg-select-language-switch"
        options={languages}
      />
    </div>
  );
}
